import { useState, useCallback, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Settings, Heart, LogOut, ChevronRight, MoreVertical, User, CreditCard, DollarSign, RefreshCw, CheckCircle, MessageSquare, Church } from 'lucide-react';
import { Dialog } from '@headlessui/react';
import { useAuth } from '@/components/AuthProvider';
import { getUserData, toPathSegment, isPathAvailable, generateUniquePath, updateUserData } from '@/lib/firebase/user';
import { useMissionPage } from '@/hooks/useMissionPage';
import { publishMissionPage, setGivingEnabled } from '@/lib/firebase/mission';
import { getUserPosts } from '@/lib/firebase/posts';
import type { Post } from '@/lib/firebase/posts/types';
import { toast } from 'sonner';
import PagePublishBanner from '@/components/PagePublishBanner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from '@/lib/utils/styles';
import { useTranslation } from 'react-i18next';
import CreatorWelcomeModal from '@/components/dashboard/CreatorWelcomeModal';
import FeedbackModal from '@/components/dashboard/FeedbackModal';
import CountrySelectionModal from '@/components/CountrySelectionModal';
import CountryNotAvailableModal from '@/components/CountryNotAvailableModal';
import StripeVerificationModal from '@/components/dashboard/StripeVerificationModal';
import { startStripeOnboarding, fetchStripeVerificationStatus, syncStripeStatus, type StripeStatusResult } from '@/lib/firebase/payments';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';

export default function DashboardLayout() {
  const { user, userRoles, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { missionPage, setMissionPage } = useMissionPage(user?.uid);
  const [posts, setPosts] = useState<Post[]>([]);
  const { t } = useTranslation();
  const [showWelcome, setShowWelcome] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [hasPaymentSetup, setHasPaymentSetup] = useState<boolean>(false);
  const [paymentStatus, setPaymentStatus] = useState<'not_set' | 'pending_verification' | 'verified' | 'failed'>('not_set');
  const [pathValue, setPathValue] = useState('');
  const [pathError, setPathError] = useState<string | null>(null);
  const [isSavingPath, setIsSavingPath] = useState(false);
  // Track any actionable issues returned from Stripe sync
  const [paymentIssues, setPaymentIssues] = useState<string[]>([]);
  // UX: show redirecting modal when sending user to Stripe
  const [isRedirecting, setIsRedirecting] = useState(false);
  // UX: show next-step modal after successful bank connect but ID still required
  const [showIdVerificationNextStep, setShowIdVerificationNextStep] = useState(false);
  // UX: celebration modal when verification completes
  const [showVerifiedModal, setShowVerifiedModal] = useState(false);
  // Country selection modal for Stripe onboarding
  const [showCountryModal, setShowCountryModal] = useState(false);
  // Country not available modal
  const [showCountryNotAvailableModal, setShowCountryNotAvailableModal] = useState(false);
  const [selectedCountryName, setSelectedCountryName] = useState('');
  // Stripe verification modal with detailed requirements
  const [showStripeVerificationModal, setShowStripeVerificationModal] = useState(false);
  const [stripeVerificationData, setStripeVerificationData] = useState<StripeStatusResult | null>(null);

  const triggerConfetti = () => {
    try {
      const count = 120;
      const colors = ['#16a34a', '#22c55e', '#86efac', '#34d399'];
      const els: HTMLElement[] = [];
      for (let i = 0; i < count; i++) {
        const el = document.createElement('div');
        el.style.position = 'fixed';
        el.style.top = '-10px';
        el.style.left = Math.random() * 100 + 'vw';
        el.style.width = '8px';
        el.style.height = '14px';
        el.style.opacity = '0.9';
        el.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
        el.style.pointerEvents = 'none';
        el.style.transform = `translateY(0) rotate(${Math.random() * 360}deg)`;
        el.style.animation = `df-fall ${1.5 + Math.random()}s linear ${Math.random()}s forwards`;
        document.body.appendChild(el);
        els.push(el);
      }
      setTimeout(() => els.forEach(e => e.remove()), 3000);
    } catch { }
  };

  // Check if we're on the main dashboard/My Page
  const isMyPage = location.pathname === '/creator' || location.pathname === '/creator/';

  const loadPosts = useCallback(async () => {
    if (!user?.uid) return;
    const userPosts = await getUserPosts(user.uid);
    setPosts(userPosts);
  }, [user?.uid]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  useEffect(() => {
    async function evaluateOnLoad() {
      if (!user?.uid || !userRoles?.creator) return;
      try {
        // Determine setup by presence of stripeAccountId on users doc
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        const stripeAccountId = userSnap.exists() ? (userSnap.data() as any)?.stripeAccountId : undefined;
        const stripeAccountIdTest = userSnap.exists() ? (userSnap.data() as any)?.stripeAccountIdTest : undefined;
        const stripeAccountStatus = userSnap.exists() ? (userSnap.data() as any)?.stripeAccountStatus : undefined;

        // Treat either test or live account as "setup" so localhost users who onboarded
        // in test mode don't get stuck seeing the payout prompt.
        const hasSetup = Boolean(stripeAccountId || stripeAccountIdTest);
        setHasPaymentSetup(hasSetup);
        // TSF-176 (ported): keep the public page doc's givingEnabled in step with
        // reality, so a donor on a draft-published page is told the truth, and a
        // creator who has since finished Stripe gets their donate form back.
        void setGivingEnabled(user.uid, hasSetup);
        const status = await fetchStripeVerificationStatus(user.uid);
        setPaymentStatus(status);

        console.log('[DashboardLayout] Status check:', {
          stripeAccountId,
          stripeAccountIdTest,
          stripeAccountStatus,
          status,
          hasSetup
        });

        // If user has Stripe account but it's not verified, check for action items
        if (hasSetup && status !== 'verified') {
          try {
            const result = await syncStripeStatus();
            setPaymentIssues(result.issues || []);
            setStripeVerificationData(result);

            console.log('[DashboardLayout] Sync result:', {
              status: result.status,
              issuesCount: result.issues?.length || 0,
              issues: result.issues,
              chargesEnabled: result.chargesEnabled,
              payoutsEnabled: result.payoutsEnabled,
              currentlyDue: result.requirements.currently_due.length,
              eventuallyDue: result.requirements.eventually_due.length,
              pendingVerification: result.requirements.pending_verification.length
            });

            // Show verification modal if there are ANY pending requirements (currently_due OR eventually_due)
            const hasPendingRequirements =
              result.requirements.currently_due.length > 0 ||
              result.requirements.eventually_due.length > 0;

            if (hasPendingRequirements) {
              console.log('[DashboardLayout] Will show verification modal - pending requirements found');
              // Small delay to ensure the page has loaded
              setTimeout(() => {
                setShowStripeVerificationModal(true);
              }, 500);
            }
          } catch (e) {
            console.error('[DashboardLayout] Error syncing Stripe status:', e);
          }
        } else {
          setPaymentIssues([]);
        }
        // TSF-84/TSF-176 (swept from sportfund 2026-09-10): do NOT front-load
        // the bank/KYC ask. A brand-new creator used to land here and get this
        // modal immediately, headed "Required: Set up payouts to receive
        // contributions" -- the very first thing the product ever says to them,
        // before a bio, a photo, or any sense of what they signed up for. It
        // also re-popped on EVERY mount, with no dismissed flag at all.
        //
        // It now waits until they have actually started building the page (a
        // bio or a profile photo), and only fires once per user. The persistent
        // payout banner below already covers ongoing reminders.
        const dismissedKey = `df_welcome_modal_dismissed_${user.uid}`;
        const alreadyDismissed = localStorage.getItem(dismissedKey) === '1';
        const u = userSnap.exists() ? (userSnap.data() as any) : {};
        const hasStartedBuilding = Boolean(u?.biography || u?.photoURL);
        setShowWelcome(!hasSetup && !alreadyDismissed && hasStartedBuilding);
      } catch (e) {
        console.error('[DashboardLayout] Error in evaluateOnLoad:', e);
      }
    }
    evaluateOnLoad();
  }, [user?.uid, userRoles?.creator]);

  // If returning from Stripe (?onboarding=complete), auto-sync status once
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('onboarding') === 'complete' && paymentStatus !== 'verified') {
      void (async () => {
        try {
          const result = await syncStripeStatus();
          setPaymentStatus(result.status as any);
          setPaymentIssues(result.issues || []);
          setStripeVerificationData(result);

          if (result.status === 'verified') {
            // Only show the publish CTA if the mission page is NOT published
            const canShowPublishCta = !!missionPage && missionPage.isPublished === false;
            if (canShowPublishCta) {
              triggerConfetti();
              setShowVerifiedModal(true);
            }
            setIsRedirecting(false);
            toast.success('Your Stripe account is verified! You can now publish and receive donations.');
          } else {
            // Show the detailed verification modal if verification is incomplete
            setShowStripeVerificationModal(true);
            setIsRedirecting(false);
          }
        } catch { /* ignore */ }
      })();
    }
  }, [location.search, paymentStatus, missionPage?.isPublished]);

  // Stripe sends the creator back here with ?onboarding=refresh (not
  // ?onboarding=complete) when the account link expired or they left the
  // hosted flow before finishing -- this used to go completely unhandled, so
  // every one of those bounces vanished with no toast at all, indistinguishable
  // from someone who never clicked "Connect Bank" (ported from sportfund TSF-325-kyc).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('onboarding') === 'refresh') {
      toast.info(t('creator.checklist.onboardingRefreshToast', {
        defaultValue: "Didn't finish setting up payouts? No problem — click Connect Bank again whenever you're ready.",
      }));
    }
  }, [location.search, t]);

  useEffect(() => {
    async function loadPath() {
      if (!user?.uid) return;
      try {
        const data = await getUserData(user.uid);
        const existingPath = (data as any)?.path || '';
        setPathValue(existingPath);
        // Backfill missing public mapping for older accounts
        if (existingPath) {
          try {
            await setDoc(doc(db, 'paths', existingPath), { userId: user.uid }, { merge: true });
          } catch { /* ignore */ }
        }
      } catch { }
    }
    loadPath();
  }, [user?.uid]);

  const handlePublish = async () => {
    if (!user?.uid) return;
    // TSF-176 (ported): a page publishes without Stripe. What used to happen here
    // -- bounce the creator into the payout modal, or block on
    // charges/payouts/currently_due -- is what kept new pages from ever going
    // live. Stripe status is still read, but only to record it and to tell the
    // creator giving is not on yet.
    let verificationResult: StripeStatusResult | null = null;
    if (hasPaymentSetup && paymentStatus !== 'verified') {
      try {
        const result = await syncStripeStatus();
        verificationResult = result;
        setStripeVerificationData(result);
      } catch { /* publishing does not depend on this */ }
    }
    try {
      // Mark payments as verified only when Stripe really allows charges AND payouts
      // with nothing currently due. firestore.rules' paymentGateFieldsOk enforces the
      // same test against the server-written stripe_accounts/{uid} doc, so writing it
      // on a half-onboarded account is rejected -- and would take the publish down
      // with it now that publishing no longer waits for Stripe.
      const stripeAllowsPublish = !!verificationResult
        && verificationResult.chargesEnabled
        && verificationResult.payoutsEnabled
        && verificationResult.requirements.currently_due.length === 0;
      if (stripeAllowsPublish && paymentStatus !== 'verified') {
        try {
          await setDoc(doc(db, 'users', user.uid), {
            paymentsVerified: true,
            stripeAccountStatus: verificationResult!.status || 'pending_verification',
          }, { merge: true });
        } catch { /* never block the publish on a bookkeeping write */ }
      }

      // Ensure public path mapping exists before publish
      try {
        const data = await getUserData(user.uid);
        const existingPath = (data as any)?.path;
        if (existingPath) {
          await setDoc(doc(db, 'paths', existingPath), { userId: user.uid }, { merge: true });
        }
      } catch { /* ignore */ }
      await publishMissionPage(user.uid, true);
      setMissionPage(prev => prev ? { ...prev, isPublished: true } : null);
      toast.success(t('creator.checklist.publishSuccess'));
    } catch (error) {
      toast.error(t('creator.checklist.publishFailed'));
    }
  };

  const handleUnpublish = async () => {
    if (!user?.uid) return;
    try {
      await publishMissionPage(user.uid, false);
      setMissionPage(prev => prev ? { ...prev, isPublished: false } : null);
      toast.success(t('creator.checklist.publishSuccess'));
    } catch (error) {
      toast.error(t('creator.checklist.publishFailed'));
    }
  };

  const handleWelcomeContinue = async () => {
    if (!user?.uid) return;
    setShowWelcome(false);
    setShowIdVerificationNextStep(false);

    // If user already has a Stripe account, try to skip country selection and go directly to Stripe
    if (hasPaymentSetup) {
      console.log('[DashboardLayout] User already has Stripe account, attempting to continue without country selection');
      setIsRedirecting(true);
      try {
        const url = await startStripeOnboarding();
        window.location.href = url;
      } catch (error: any) {
        setIsRedirecting(false);
        console.error('[DashboardLayout] Error starting onboarding:', error);

        // If the error is about missing country, show country selection
        if (error?.message?.includes('Country must be specified') || error?.message?.includes('failed-precondition')) {
          console.log('[DashboardLayout] Country not found, showing country selection');
          setShowCountryModal(true);
        } else {
          toast.error(error?.message || 'Failed to continue Stripe setup');
        }
      }
    } else {
      // First time setup - show country selection
      console.log('[DashboardLayout] First time setup, showing country selection');
      setShowCountryModal(true);
    }
  };

  const handleCountrySelect = async (countryCode: string) => {
    if (!user?.uid) return;

    console.log('[DashboardLayout] Country selected:', countryCode);

    // Get the country name for display
    const STRIPE_COUNTRIES = [
      { code: 'US', name: 'United States' },
      { code: 'CA', name: 'Canada' },
      { code: 'GB', name: 'United Kingdom' },
      { code: 'AU', name: 'Australia' },
      { code: 'AT', name: 'Austria' },
      { code: 'BE', name: 'Belgium' },
      { code: 'BG', name: 'Bulgaria' },
      { code: 'HR', name: 'Croatia' },
      { code: 'CY', name: 'Cyprus' },
      { code: 'CZ', name: 'Czech Republic' },
      { code: 'DK', name: 'Denmark' },
      { code: 'EE', name: 'Estonia' },
      { code: 'FI', name: 'Finland' },
      { code: 'FR', name: 'France' },
      { code: 'DE', name: 'Germany' },
      { code: 'GI', name: 'Gibraltar' },
      { code: 'GR', name: 'Greece' },
      { code: 'HK', name: 'Hong Kong' },
      { code: 'HU', name: 'Hungary' },
      { code: 'IE', name: 'Ireland' },
      { code: 'IT', name: 'Italy' },
      { code: 'JP', name: 'Japan' },
      { code: 'LV', name: 'Latvia' },
      { code: 'LI', name: 'Liechtenstein' },
      { code: 'LT', name: 'Lithuania' },
      { code: 'LU', name: 'Luxembourg' },
      { code: 'MT', name: 'Malta' },
      { code: 'MX', name: 'Mexico' },
      { code: 'NL', name: 'Netherlands' },
      { code: 'NZ', name: 'New Zealand' },
      { code: 'NO', name: 'Norway' },
      { code: 'PL', name: 'Poland' },
      { code: 'PT', name: 'Portugal' },
      { code: 'RO', name: 'Romania' },
      { code: 'SG', name: 'Singapore' },
      { code: 'SK', name: 'Slovakia' },
      { code: 'SI', name: 'Slovenia' },
      { code: 'ES', name: 'Spain' },
      { code: 'SE', name: 'Sweden' },
      { code: 'CH', name: 'Switzerland' },
      { code: 'AE', name: 'United Arab Emirates' },
    ];
    const country = STRIPE_COUNTRIES.find(c => c.code === countryCode);
    const countryName = country?.name || countryCode;

    // TEMPORARILY COMMENTED OUT FOR CROSS-BORDER TESTING
    // Allow all countries to proceed with Stripe onboarding
    setShowCountryModal(false);
    setIsRedirecting(true);
    try {
      console.log('[DashboardLayout] Starting Stripe onboarding for country:', countryCode);
      const url = await startStripeOnboarding(countryCode);
      console.log('[DashboardLayout] Got onboarding URL:', url);
      window.location.href = url;
    } catch (e: any) {
      console.error('[DashboardLayout] Error starting onboarding:', e);
      const errorMsg = e?.message || 'Failed to start onboarding';
      toast.error(errorMsg);
      setIsRedirecting(false);
    }

    // ORIGINAL RESTRICTION CODE (commented out for testing):
    // // If US, proceed with Stripe onboarding
    // if (countryCode === 'US') {
    //   setIsRedirecting(true);
    //   try {
    //     const url = await startStripeOnboarding(countryCode);
    //     window.location.href = url;
    //   } catch (e) {
    //     toast.error('Failed to start onboarding');
    //     setIsRedirecting(false);
    //   }
    // } else {
    //   // Store the country in Firebase
    //   try {
    //     await updateUserData(user.uid, { preferredCountry: countryCode } as any);
    //     setSelectedCountryName(countryName);
    //     setShowCountryNotAvailableModal(true);
    //   } catch (e) {
    //     toast.error('Failed to save country preference');
    //   }
    // }
  };

  const handlePreview = async () => {
    if (!user?.uid) return;
    try {
      const data = await getUserData(user.uid);
      const path = (data as any)?.path;
      const url = path ? `/mission/${path}` : `/mission/${user.uid}`;
      window.open(url, '_blank');
    } catch {
      window.open(`/mission/${user.uid}`, '_blank');
    }
  };

  const handlePathSave = async () => {
    if (!user?.uid) return;
    setPathError(null);
    setIsSavingPath(true);
    try {
      // Enforce non-empty path; if empty, generate from name
      let cleaned = pathValue.trim();
      if (!cleaned) {
        cleaned = await generateUniquePath(user?.displayName?.split(' ')[0] || '', user?.displayName?.split(' ').slice(1).join(' ') || '', user.uid);
      } else {
        cleaned = toPathSegment(cleaned);
        const available = await isPathAvailable(cleaned, user.uid);
        if (!available) {
          setPathError('This URL is not available');
          return;
        }
      }
      await updateUserData(user.uid, { path: cleaned } as any);
      // Create or update public path mapping for anonymous resolution
      await setDoc(doc(db, 'paths', cleaned), { userId: user.uid }, { merge: true });
      setPathValue(cleaned);
      toast.success('Public URL updated');
    } catch {
      setPathError('Failed to save URL');
    } finally {
      setIsSavingPath(false);
    }
  };

  const handleCheckStatus = async () => {
    if (!user?.uid) return;
    try {
      const result = await syncStripeStatus();
      setPaymentStatus(result.status as any);
      setPaymentIssues(result.issues || []);
      setStripeVerificationData(result);

      if (result.status === 'verified') {
        triggerConfetti();
        // Only show modal CTA if mission page is not published
        if (missionPage?.isPublished === false) {
          setShowVerifiedModal(true);
        }
        toast.success('Your Stripe account is verified! You can now publish and receive donations.');
      } else {
        // Show the detailed verification modal
        setShowStripeVerificationModal(true);
      }
    } catch (e: any) {
      toast.error(e?.message || 'Failed to check status');
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* The sidebar is 256px wide and fixed, and the content carried an
          unconditional ml-64 to clear it. On a 390px phone that left 134px
          of usable width -- measured on production 2026-09-24: the content
          box was still 476px wide and began at x=256, so 345px of every
          creator screen sat off the right edge. A real athlete wrote in from
          /creator/donations on 2026-09-23: "will the support option only
          generate on desktop view? the link doesn't work when opened from a
          phone." Below md the two stack; the content also needs min-w-0,
          because a flex child defaults to min-width:auto and will happily
          grow past its parent. */}
      {/* Left sidebar */}
      <div className="w-full md:w-64 bg-white border-b md:border-b-0 md:border-r flex flex-col md:fixed md:h-full max-h-[45vh] md:max-h-none overflow-y-auto md:overflow-visible">
        {/* Scrollable navigation area */}
        <div className="flex-1 px-4 py-4 overflow-y-auto">
          {/* Logo */}
          <div className="mb-6 flex justify-start px-4 py-2">
            <button
              onClick={() => navigate('/creator')}
              className="focus:outline-none hover:opacity-80 transition-opacity"
            >
              <img src="/devotionfund-logo.png" alt="Devotion Fund Logo" className="h-8" />
            </button>
          </div>

          {/* Navigation items */}
          <nav className="space-y-1">
            <button
              onClick={() => navigate('/creator')}
              className={cn(
                "flex items-center w-full px-2 py-2 text-sm font-medium rounded-md",
                location.pathname === '/creator' || location.pathname === '/creator/'
                  ? "bg-gray-100 text-gray-900"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              )}
            >
              <User className="mr-3 h-5 w-5" />
              {t('creator.nav.myPage')}
            </button>

            <button
              onClick={() => navigate('/creator/content')}
              className={cn(
                "flex items-center w-full px-2 py-2 text-sm font-medium rounded-md",
                location.pathname.startsWith('/creator/content')
                  ? "bg-gray-100 text-gray-900"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              )}
            >
              <Heart className="mr-3 h-5 w-5" />
              {t('creator.nav.content')}
            </button>

            <button
              onClick={() => navigate('/creator/donations')}
              className={cn(
                "flex items-center w-full px-2 py-2 text-sm font-medium rounded-md",
                location.pathname === '/creator/donations'
                  ? "bg-gray-100 text-gray-900"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              )}
            >
              <DollarSign className="mr-3 h-5 w-5" />
              {t('creator.nav.donations')}
            </button>

            <button
              onClick={() => navigate('/creator/membership')}
              className={cn(
                "flex items-center w-full px-2 py-2 text-sm font-medium rounded-md",
                location.pathname === '/creator/membership'
                  ? "bg-gray-100 text-gray-900"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              )}
            >
              <CreditCard className="mr-3 h-5 w-5" />
              {t('creator.nav.membership')}
            </button>

            <button
              onClick={() => navigate('/creator/audience')}
              className={cn(
                "flex items-center w-full px-2 py-2 text-sm font-medium rounded-md",
                location.pathname === '/creator/audience'
                  ? "bg-gray-100 text-gray-900"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              )}
            >
              <User className="mr-3 h-5 w-5" />
              {t('creator.nav.audience')}
            </button>

            <button
              onClick={() => navigate('/creator/settings')}
              className={cn(
                "flex items-center w-full px-2 py-2 text-sm font-medium rounded-md",
                location.pathname === '/creator/settings'
                  ? "bg-gray-100 text-gray-900"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              )}
            >
              <Settings className="mr-3 h-5 w-5" />
              {t('creator.nav.settings')}
            </button>

            {userRoles?.church && (
              <button
                onClick={() => navigate('/church')}
                className="flex items-center w-full px-2 py-2 text-sm font-medium rounded-md text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              >
                <Church className="mr-3 h-5 w-5" />
                My church
              </button>
            )}

            <button
              type="button"
              onClick={() => setShowFeedbackModal(true)}
              className="flex items-center w-full px-2 py-2 text-sm font-medium rounded-md text-gray-600 hover:bg-gray-50 hover:text-gray-900"
            >
              <MessageSquare className="mr-3 h-5 w-5" />
              Send feedback
            </button>
          </nav>
        </div>

        {/* Fixed user profile section */}
        <div className="border-t p-4 bg-white">
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center w-full hover:bg-gray-50 rounded-lg p-2 transition-colors">
              <div className="flex-shrink-0">
                {user?.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt={user.displayName || ''}
                    className="h-10 w-10 rounded-full"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-lg font-medium">
                    {user?.firstName?.[0] || user?.displayName?.[0] || user?.email?.[0] || 'U'}
                  </div>
                )}
              </div>
              <div className="ml-3 flex-1 text-left">
                <p className="text-sm font-medium text-gray-900">
                  {user?.firstName && user?.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : user?.displayName || user?.email}
                </p>
                <p className="text-xs text-gray-500">{t('creator.profile.label')}</p>
              </div>
              <MoreVertical className="h-5 w-5 text-gray-400" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {/* DF-306: both items used to show the identical bold name line with
                  only a tiny gray caption ("Dashboard" vs "Creator") telling them
                  apart -- easy to miss at a glance. The destination is now the
                  bold line and the name is the caption, so the two items read as
                  different at a glance instead of as duplicates. */}
              <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{t('nav.dashboard')}</span>
                  <span className="text-xs text-gray-500">
                    {user?.firstName && user?.lastName
                      ? `${user.firstName} ${user.lastName}`
                      : user?.displayName}
                  </span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/creator')}>
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{t('creator.nav.myPage')}</span>
                  <span className="text-xs text-gray-500">
                    {user?.firstName && user?.lastName
                      ? `${user.firstName} ${user.lastName}`
                      : user?.displayName}
                  </span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => signOut()}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <LogOut className="mr-2 h-4 w-4" />
                {t('actions.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 min-w-0 md:ml-64">
        {/* Country selection modal */}
        <CountrySelectionModal
          isOpen={showCountryModal}
          onClose={() => setShowCountryModal(false)}
          onSelect={handleCountrySelect}
        />

        {/* Country not available modal */}
        <CountryNotAvailableModal
          isOpen={showCountryNotAvailableModal}
          onClose={() => setShowCountryNotAvailableModal(false)}
          countryName={selectedCountryName}
        />

        {/* Stripe verification requirements modal */}
        {stripeVerificationData && (
          <StripeVerificationModal
            isOpen={showStripeVerificationModal}
            onClose={() => setShowStripeVerificationModal(false)}
            onContinueSetup={handleWelcomeContinue}
            requirements={stripeVerificationData.requirements}
            chargesEnabled={stripeVerificationData.chargesEnabled}
            payoutsEnabled={stripeVerificationData.payoutsEnabled}
            disabledReason={stripeVerificationData.disabledReason}
          />
        )}

        {/* Redirecting to Stripe modal */}
        <Dialog open={isRedirecting} onClose={() => { }} className="relative z-[60]">
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="mx-auto w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
              <div className="flex items-center gap-3">
                <RefreshCw className="h-5 w-5 animate-spin text-blue-600" />
                <Dialog.Title className="text-base font-semibold text-gray-900">Redirecting to Stripe…</Dialog.Title>
              </div>
              <p className="mt-3 text-sm text-gray-600">You are being redirected to Stripe to securely set up your payments.</p>
            </Dialog.Panel>
          </div>
        </Dialog>

        {/* Next step: identity verification modal */}
        <Dialog open={showIdVerificationNextStep} onClose={() => setShowIdVerificationNextStep(false)} className="relative z-[110]">
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="mx-auto w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 flex items-center justify-center rounded-full bg-green-100">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <Dialog.Title className="text-lg font-semibold text-gray-900">Bank account connected!</Dialog.Title>
                  <p className="mt-2 text-sm text-gray-700">Great job. Last step: verify your personal identification in Stripe. They will ask for a government ID and ID number.</p>
                </div>
              </div>
              <div className="mt-6 flex gap-2 justify-end">
                <button onClick={() => setShowIdVerificationNextStep(false)} className="text-sm px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">Later</button>
                <button onClick={handleWelcomeContinue} className="text-sm bg-primary text-white px-3 py-1.5 rounded-md hover:bg-primary/90">Continue in Stripe</button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
        {/* Verified celebration modal; only show when page is not yet published */}
        <Dialog open={showVerifiedModal && (missionPage?.isPublished === false)} onClose={() => setShowVerifiedModal(false)} className="relative z-[110]">
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="mx-auto w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 flex items-center justify-center rounded-full bg-green-100">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <Dialog.Title className="text-lg font-semibold text-gray-900">You're all set!</Dialog.Title>
                  <p className="mt-2 text-sm text-gray-700">Your Stripe account is verified. You can now publish your page and start sharing it with friends and family.</p>
                </div>
              </div>
              <div className="mt-6 flex gap-2 justify-end">
                <button onClick={() => setShowVerifiedModal(false)} className="text-sm px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">Close</button>
                <button onClick={() => { setShowVerifiedModal(false); handlePublish(); }} className="text-sm bg-primary text-white px-3 py-1.5 rounded-md hover:bg-primary/90">Publish my page</button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
        <CreatorWelcomeModal
          isOpen={showWelcome}
          onClose={() => {
            // Remember the dismissal, or the modal re-pops on every mount.
            if (user?.uid) localStorage.setItem(`df_welcome_modal_dismissed_${user.uid}`, '1');
            setShowWelcome(false);
          }}
          onContinue={handleWelcomeContinue}
        />
        <FeedbackModal
          isOpen={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          userId={user?.uid}
        />
        {(() => {
          // Scenario 1: Not started - no Stripe account
          if (paymentStatus === 'not_set') {
            return (
              <div className="bg-yellow-50 border-b border-yellow-200">
                <div className="px-4 py-3 flex items-center justify-between">
                  <div className="text-sm text-yellow-900">
                    {/* MSF-222: this is the first sentence a new creator reads, and it
                        used to put payouts before the page -- which was literally true
                        while publishing was gated on Stripe, and is the reason nobody
                        published. The page comes first now, so say so. */}
                    <span>Your page can go live now &mdash; publish and share it whenever you like. Set up payouts with Stripe when you are ready, and supporters can start giving.</span>
                  </div>
                  <div className="shrink-0 flex gap-2">
                    <button onClick={handleWelcomeContinue} className="text-sm bg-primary text-white px-3 py-1.5 rounded-md hover:bg-primary/90">Set up now</button>
                  </div>
                </div>
              </div>
            );
          }

          // Scenario 2: Pending verification with stripe data
          if (paymentStatus === 'pending_verification' && stripeVerificationData) {
            const hasCurrentlyDue = stripeVerificationData.requirements.currently_due.length > 0;
            const hasEventuallyDue = stripeVerificationData.requirements.eventually_due.length > 0;
            const hasPendingVerification = stripeVerificationData.requirements.pending_verification.length > 0;
            const canAcceptPayments = stripeVerificationData.chargesEnabled && !hasCurrentlyDue;

            // Scenario 2a: Can accept payments but has eventually_due (like ID verification)
            if (canAcceptPayments && hasEventuallyDue) {
              return (
                <div className="bg-blue-50 border-b border-blue-200">
                  <div className="px-4 py-3 flex items-center justify-between">
                    <div className="text-sm text-blue-900">
                      <span>✓ You're accepting donations! Complete identity verification soon to avoid service disruptions.</span>
                    </div>
                    <div className="shrink-0 flex gap-2">
                      <button onClick={handleWelcomeContinue} className="text-sm bg-primary text-white px-3 py-1.5 rounded-md hover:bg-primary/90">Complete verification</button>
                    </div>
                  </div>
                </div>
              );
            }

            // Scenario 2b: Has currently_due items (blocking)
            if (hasCurrentlyDue) {
              return (
                <div className="bg-yellow-50 border-b border-yellow-200">
                  <div className="px-4 py-3 flex items-center justify-between">
                    <div className="text-sm text-yellow-900">
                      <span>Action required: Complete the required steps to start receiving donations.</span>
                    </div>
                    <div className="shrink-0 flex gap-2">
                      <button onClick={handleWelcomeContinue} className="text-sm bg-primary text-white px-3 py-1.5 rounded-md hover:bg-primary/90">Complete setup</button>
                    </div>
                  </div>
                </div>
              );
            }

            // Scenario 2c: Only pending verification (Stripe is reviewing)
            if (hasPendingVerification && !hasEventuallyDue && !hasCurrentlyDue) {
              return (
                <div className="bg-blue-50 border-b border-blue-200">
                  <div className="px-4 py-3 flex items-center justify-between">
                    <div className="text-sm text-blue-900">
                      <span>Your verification is being reviewed by Stripe. This usually takes 1-3 business days.</span>
                    </div>
                    <div className="shrink-0 flex gap-2">
                      <button onClick={handleCheckStatus} className="text-sm bg-gray-100 text-gray-800 px-3 py-1.5 rounded-md hover:bg-gray-200">Check status</button>
                    </div>
                  </div>
                </div>
              );
            }
          }

          // Scenario 2d: Pending but no detailed data yet (fallback)
          if (paymentStatus === 'pending_verification') {
            return (
              <div className="bg-yellow-50 border-b border-yellow-200">
                <div className="px-4 py-3 flex items-center justify-between">
                  <div className="text-sm text-yellow-900">
                    <span>Payment setup in progress. Click to check status.</span>
                  </div>
                  <div className="shrink-0 flex gap-2">
                    <button onClick={handleCheckStatus} className="text-sm bg-primary text-white px-3 py-1.5 rounded-md hover:bg-primary/90">Check status</button>
                  </div>
                </div>
              </div>
            );
          }

          // Scenario 3: Failed status
          if (paymentStatus === 'failed') {
            return (
              <div className="bg-red-50 border-b border-red-200">
                <div className="px-4 py-3 flex items-center justify-between">
                  <div className="text-sm text-red-900">
                    <span>Payment account issue detected. Please review and fix the issues.</span>
                  </div>
                  <div className="shrink-0 flex gap-2">
                    <button onClick={handleWelcomeContinue} className="text-sm bg-red-600 text-white px-3 py-1.5 rounded-md hover:bg-red-700">Review issues</button>
                  </div>
                </div>
              </div>
            );
          }

          // Scenario 4: Fully verified - don't show banner
          return null;
        })()}
        {/* An unpublished page is invisible: no URL worth sharing, and no way
            for anyone to give. This prompt used to render only on the My Page
            route, so a creator who built their page and moved on was never
            told again. Two Sport Fund creators finished Stripe onboarding and
            still never published; five My Study Fund students never published
            at all. Show it wherever they are, until the page is live. */}
        {/* Unknown is not false: require the page to have LOADED before saying
            anything about the publish state. */}
        {missionPage && (isMyPage || missionPage.isPublished === false) && (
          <PagePublishBanner
            isPublished={missionPage.isPublished === true}
            onPublish={handlePublish}
            onUnpublish={handleUnpublish}
            onPreview={handlePreview}
            pathValue={pathValue}
            onPathChange={(val) => { setPathValue(toPathSegment(val)); setPathError(null); }}
            onPathSave={handlePathSave}
            pathError={pathError}
            isSavingPath={isSavingPath}
          />
        )}
        <main>
          <div className="max-w-5xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
